/**
 * useConnectStream — thin React wrapper around the module-scope `streamsStore`.
 *
 * All SDK subscriptions and call-state logic live in `streamsStore` and
 * `streamsBootstrap`. This hook reads the snapshot via `useSyncExternalStore`
 * and forwards store actions to the caller.
 */
import { useSyncExternalStore, useEffect, useRef } from 'react';
import type {
    AgentStatus,
    ContactInfo,
    VideoCapabilities,
    ChimeMeetingInfo,
    VoiceEnhancementMode,
    CcpAuthState,
    NetworkConnectionHealth,
    MissedCallNotice,
} from '../types/amazon-connect-streams';
import { streamsStore } from '../services/streamsStore';
import type { ConferenceParticipant as StoreConferenceParticipant, AudioDeviceOption, SoftphoneFailureNotice, MicPermissionState } from '../services/streamsStore';

// Re-export so downstream consumers keep their current import paths.
export type ConferenceParticipant = StoreConferenceParticipant;
export type { AudioDeviceOption, SoftphoneFailureNotice, MicPermissionState };

export interface UseConnectStreamOptions {
    enabled?: boolean;
    onAgentInitialized?: () => void;
    onLogout?: () => void;
    onCallConnected?: () => void;
    onCallEnded?: () => void;
    onSoftphoneFailure?: (failure: SoftphoneFailureNotice) => void;
}

export interface UseConnectStreamResult {
    // Initialization / auth
    ccpAuthState: CcpAuthState;
    networkConnection: NetworkConnectionHealth;
    browserOnline: boolean;
    /** Probe-backed reachability (see streamsStore.networkReachable). */
    networkReachable: boolean;
    lastMissedCall: MissedCallNotice | null;
    hasTwoActiveParticipants: boolean;
    isUnderSupervision: boolean;

    // Agent
    agentName: string;
    agentEmail: string;
    agentStatus: AgentStatus;
    isAgentMuted: boolean;
    voiceEnhancementMode: VoiceEnhancementMode;
    microphoneDeviceId: string | null;
    speakerDeviceId: string | null;
    microphones: AudioDeviceOption[];
    speakers: AudioDeviceOption[];

    // Contact
    contact: ContactInfo | null;
    isIncomingCall: boolean;
    isConnected: boolean;
    isConnectionFailed: boolean;
    callDuration: number;
    isCallOnHold: boolean;
    isPendingAutoAccept: boolean;
    canTransferContact: boolean | null;
    softphoneFailure: SoftphoneFailureNotice | null;
    micPermission: MicPermissionState;
    networkDropNotice: boolean;

    // Video
    videoCapabilities: VideoCapabilities | null;
    videoConnectionInfo: ChimeMeetingInfo | null;

    // Conference
    conferenceParticipants: ConferenceParticipant[];
    isAddingParticipant: boolean;
    dialableCountries: string[];

    // Agent Actions
    setAgentAvailable: (options?: { force?: boolean }) => void;
    setAgentOffline: () => void;
    toggleAgentMute: () => void;
    logoutAgent: () => Promise<void>;
    setVoiceEnhancementMode: (mode: VoiceEnhancementMode) => Promise<void>;
    refreshAudioDevices: () => Promise<void>;
    setMicrophoneDevice: (deviceId: string) => Promise<void>;
    setSpeakerDevice: (deviceId: string) => Promise<void>;
    clearSoftphoneFailure: () => void;
    dismissConnectionFailure: () => void;
    ensureMicrophoneReady: () => Promise<boolean>;
    refreshMicPermission: () => Promise<MicPermissionState>;
    dismissNetworkDropNotice: () => void;

    // Contact Actions
    acceptCall: () => void;
    rejectCall: () => void;
    setShouldAutoAcceptCall: (value: boolean) => void;
    endCall: () => void;
    clearContact: () => void;
    toggleCallHold: () => void;
    dismissMissedCall: () => void;

    // Conference Actions
    addParticipant: (phoneNumber: string) => Promise<void>;
    holdParticipant: (connectionId: string) => void;
    resumeParticipant: (connectionId: string) => void | Promise<void>;
    disconnectParticipant: (connectionId: string) => void;
    conferenceAll: () => void;
    refreshParticipants: () => void;

    // Dialpad
    sendDtmf: (digits: string) => void;
    dialNumber: (phoneNumber: string) => Promise<void>;
}

export const useConnectStream = (options?: UseConnectStreamOptions): UseConnectStreamResult => {
    const snapshot = useSyncExternalStore(streamsStore.subscribe, streamsStore.getSnapshot, streamsStore.getSnapshot);

    // Keep latest callbacks in a ref so the subscription below doesn't re-fire
    // on every render.
    const optionsRef = useRef(options);
    useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    // Bridge store events → hook options callbacks.
    useEffect(() => {
        return streamsStore.onEvent((event) => {
            const opts = optionsRef.current;
            if (!opts) return;
            switch (event.type) {
                case 'agent:initialized':
                    opts.onAgentInitialized?.();
                    break;
                case 'contact:connected':
                    opts.onCallConnected?.();
                    break;
                case 'contact:ended':
                    opts.onCallEnded?.();
                    break;
                case 'softphone:failure':
                    opts.onSoftphoneFailure?.(event.failure);
                    break;
                case 'logout':
                    opts.onLogout?.();
                    break;
                default:
                    break;
            }
        });
    }, []);

    const {
        ccpAuthState,
        networkConnection,
        lastMissedCall,
        hasTwoActiveParticipants,
        isUnderSupervision,
        agentName,
        agentEmail,
        agentStatus,
        isAgentMuted,
        dialableCountries,
        voiceEnhancementMode,
        canTransferContact,
        microphoneDeviceId,
        speakerDeviceId,
        microphones,
        speakers,
        softphoneFailure,
        micPermission,

        callPhase,
        contact,
        callDuration,
        isCallOnHold,
        videoCapabilities,
        videoConnectionInfo,
        conferenceParticipants,
        isAddingParticipant,
        networkDropNotice,
        browserOnline,
        networkReachable,
    } = snapshot;

    // Map callPhase back to the legacy `isIncomingCall` / `isConnected` flags.
    const isIncomingCall = callPhase === 'ringing';
    const isConnected = callPhase === 'connected';
    const isConnectionFailed = callPhase === 'failed';

    return {
        ccpAuthState,
        networkConnection,
        browserOnline,
        networkReachable,
        lastMissedCall,
        hasTwoActiveParticipants,
        isUnderSupervision,

        // Agent
        agentName,
        agentEmail,
        agentStatus,
        isAgentMuted,
        voiceEnhancementMode,
        microphoneDeviceId,
        speakerDeviceId,
        microphones,
        speakers,

        // Contact
        contact,
        isIncomingCall,
        isConnected,
        isConnectionFailed,
        callDuration,
        isCallOnHold,
        // Kept for backward compatibility; warmup logic was removed from the store.
        isPendingAutoAccept: false,
        canTransferContact,
        softphoneFailure,
        micPermission,
        networkDropNotice,

        // Video
        videoCapabilities,
        videoConnectionInfo,

        // Conference
        conferenceParticipants,
        isAddingParticipant,
        dialableCountries,

        // Agent Actions
        setAgentAvailable: streamsStore.setAgentAvailable,
        setAgentOffline: streamsStore.setAgentOffline,
        toggleAgentMute: streamsStore.toggleAgentMute,
        logoutAgent: streamsStore.logout,
        setVoiceEnhancementMode: streamsStore.setVoiceEnhancementMode,
        refreshAudioDevices: streamsStore.refreshAudioDevices,
        setMicrophoneDevice: streamsStore.setMicrophoneDevice,
        setSpeakerDevice: streamsStore.setSpeakerDevice,
        clearSoftphoneFailure: streamsStore.clearSoftphoneFailure,
        dismissConnectionFailure: streamsStore.dismissConnectionFailure,
        ensureMicrophoneReady: streamsStore.ensureMicrophoneReady,
        refreshMicPermission: streamsStore.refreshMicPermission,
        dismissNetworkDropNotice: streamsStore.dismissNetworkDropNotice,

        // Contact Actions
        acceptCall: streamsStore.acceptCurrent,
        rejectCall: streamsStore.rejectCurrent,
        setShouldAutoAcceptCall: streamsStore.setShouldAutoAcceptCall,
        endCall: streamsStore.endCurrent,
        clearContact: streamsStore.clearCurrent,
        toggleCallHold: streamsStore.toggleHoldCurrent,
        dismissMissedCall: streamsStore.dismissMissedCall,

        // Conference Actions
        addParticipant: streamsStore.addParticipant,
        holdParticipant: streamsStore.holdParticipant,
        resumeParticipant: streamsStore.resumeParticipant,
        disconnectParticipant: streamsStore.disconnectParticipant,
        conferenceAll: streamsStore.conferenceAll,
        refreshParticipants: streamsStore.refreshParticipants,

        // Dialpad
        sendDtmf: streamsStore.sendDtmf,
        dialNumber: streamsStore.dialNumber,
    };
};

export default useConnectStream;
